import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { LogoProvider } from './context/LogoContext';
import { ProtectedRoute, AdminRoute } from './components/ProtectedRoute';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SendMoneyPage from './pages/SendMoneyPage';
import RecipientsPage from './pages/RecipientsPage';
import TransferHistoryPage from './pages/TransferHistoryPage';
import ExchangeCalculatorPage from './pages/ExchangeCalculatorPage';
import KYCPage from './pages/KYCPage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';
import DepositPage from './pages/DepositPage';
import DepositSuccessPage from './pages/DepositSuccessPage';
import NotificationsPage from './pages/NotificationsPage';
import IncomeCertificatePage from './pages/IncomeCertificatePage';
import ResidenceCardPage from './pages/ResidenceCardPage';
import RecipientFormPage from './pages/RecipientFormPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminTransfersPage from './pages/admin/AdminTransfersPage';
import AdminKYCPage from './pages/admin/AdminKYCPage';
import AdminAMLPage from './pages/admin/AdminAMLPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminDepositsPage from './pages/admin/AdminDepositsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminCompanyPage from './pages/admin/AdminCompanyPage';
import AdminTermsPage from './pages/admin/AdminTermsPage';
import AdminPrivacyPage from './pages/admin/AdminPrivacyPage';
import AdminTransferMethodsPage from './pages/admin/AdminTransferMethodsPage';
import AdminPricingPage from './pages/admin/AdminPricingPage';
import AdminFAQPage from './pages/admin/AdminFAQPage';
import AdminFooterPage from './pages/admin/AdminFooterPage';
import AdminIncomeCertPage from './pages/admin/AdminIncomeCertPage';
import AdminResidenceCardPage from './pages/admin/AdminResidenceCardPage';
import AdminEmailPage from './pages/admin/AdminEmailPage';
import AdminReferralsPage from './pages/admin/AdminReferralsPage';
import AdminBalanceCertPage from './pages/admin/AdminBalanceCertPage';
import ReferralPage from './pages/user/ReferralPage';
import CompanyPage from './pages/CompanyPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import TransferMethodsPage from './pages/TransferMethodsPage';
import PricingPage from './pages/PricingPage';
import FAQPage from './pages/FAQPage';
import PublicCalculatorPage from './pages/PublicCalculatorPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

import { PWAInstallPrompt } from './components/pwa/PWAInstallPrompt';
import './App.css';

function App() {
  return (
    <LanguageProvider>
    <LogoProvider>
    <AuthProvider>
      <BrowserRouter>
        <PWAInstallPrompt />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/transfer-methods" element={<TransferMethodsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/calculator" element={<PublicCalculatorPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/recipient-form/:token" element={<RecipientFormPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Protected User Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/send" element={<ProtectedRoute><SendMoneyPage /></ProtectedRoute>} />
          <Route path="/recipients" element={<ProtectedRoute><RecipientsPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><TransferHistoryPage /></ProtectedRoute>} />
          <Route path="/exchange" element={<ProtectedRoute><ExchangeCalculatorPage /></ProtectedRoute>} />
          <Route path="/kyc" element={<ProtectedRoute><KYCPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
          <Route path="/deposit" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
          <Route path="/deposit/success" element={<ProtectedRoute><DepositSuccessPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/income-certificate" element={<ProtectedRoute><IncomeCertificatePage /></ProtectedRoute>} />
          <Route path="/residence-card" element={<ProtectedRoute><ResidenceCardPage /></ProtectedRoute>} />
          <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
          <Route path="/admin/transfers" element={<AdminRoute><AdminTransfersPage /></AdminRoute>} />
          <Route path="/admin/kyc" element={<AdminRoute><AdminKYCPage /></AdminRoute>} />
          <Route path="/admin/aml" element={<AdminRoute><AdminAMLPage /></AdminRoute>} />
          <Route path="/admin/deposits" element={<AdminRoute><AdminDepositsPage /></AdminRoute>} />
          <Route path="/admin/reports" element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
          <Route path="/admin/balance-certificates" element={<AdminRoute><AdminBalanceCertPage /></AdminRoute>} />
          <Route path="/admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
          <Route path="/admin/company" element={<AdminRoute><AdminCompanyPage /></AdminRoute>} />
          <Route path="/admin/terms" element={<AdminRoute><AdminTermsPage /></AdminRoute>} />
          <Route path="/admin/privacy" element={<AdminRoute><AdminPrivacyPage /></AdminRoute>} />
          <Route path="/admin/transfer-methods" element={<AdminRoute><AdminTransferMethodsPage /></AdminRoute>} />
          <Route path="/admin/pricing" element={<AdminRoute><AdminPricingPage /></AdminRoute>} />
          <Route path="/admin/faq" element={<AdminRoute><AdminFAQPage /></AdminRoute>} />
          <Route path="/admin/footer" element={<AdminRoute><AdminFooterPage /></AdminRoute>} />
          <Route path="/admin/income-certificates" element={<AdminRoute><AdminIncomeCertPage /></AdminRoute>} />
          <Route path="/admin/residence-cards" element={<AdminRoute><AdminResidenceCardPage /></AdminRoute>} />
          <Route path="/admin/email" element={<AdminRoute><AdminEmailPage /></AdminRoute>} />
          <Route path="/admin/referrals" element={<AdminRoute><AdminReferralsPage /></AdminRoute>} />
          
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
    </LogoProvider>
    </LanguageProvider>
  );
}

const APP_VERSION = '01.2026.04.07.2122.α';

function VersionBadge() {
  return (
    <div
      style={{
        position: 'fixed', bottom: 8, right: 12, zIndex: 9999,
        fontSize: 10, color: 'rgba(120,120,120,0.5)',
        fontFamily: 'monospace', pointerEvents: 'none', userSelect: 'none',
      }}
      data-testid="version-badge"
    >
      v{APP_VERSION}
    </div>
  );
}

export default function WrappedApp() {
  return (
    <>
      <App />
      <VersionBadge />
    </>
  );
}
